import {
  isTableAnswered,
  validateTableAnswer,
  type FormQuestion,
  type QuestionValidation,
} from "@kushya/shared";

import type {
  MultiChoiceValue,
  SingleChoiceValue,
} from "@/components/form-questions/types";
import { hasSignatureArtifact, isTypedNameMissing } from "@/utils/signature";
import type { ValidationError } from "@/api/responses";

export interface ClientValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

/**
 * Run lightweight client-side validation before submitting answers.
 * Mirrors server validation messages so the UX stays consistent.
 */
export function validateAnswersClientSide(
  questions: FormQuestion[],
  answers: Record<string, unknown>,
  skippedQuestionRefs: string[] = [],
): ClientValidationResult {
  const errors: ValidationError[] = [];
  const skipped = new Set(skippedQuestionRefs);

  for (const question of questions) {
    if (skipped.has(question.ref)) {
      continue;
    }

    const value = answers[question.ref];
    const questionErrors = validateQuestion(question, value);

    if (questionErrors.length > 0) {
      errors.push(...questionErrors);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

function validateQuestion(
  question: FormQuestion,
  value: unknown,
): ValidationError[] {
  if (
    question.type === "read_only_text" ||
    question.type === "link" ||
    question.type === "rich_text" ||
    question.type === "image" ||
    question.type === "divider" ||
    question.type === "callout"
  ) {
    return [];
  }

  const errors: ValidationError[] = [];
  const required = isQuestionRequired(question);
  const answered = isAnswered(question, value);
  const missingTypedName =
    question.type === "signature" &&
    isTypedNameMissing(
      question as Extract<FormQuestion, { type: "signature" }>,
      value,
    );

  if (required && !answered) {
    errors.push({
      questionRef: question.ref,
      questionTitle: question.title,
      message: missingTypedName
        ? "Please type your full name to confirm the signature"
        : `${question.title} is required`,
    });
    return errors;
  }

  if (!answered) {
    return errors;
  }

  if (missingTypedName) {
    errors.push({
      questionRef: question.ref,
      questionTitle: question.title,
      message: "Please type your full name to confirm the signature",
    });
  }

  const typeErrors = validateByType(question, value);
  if (typeErrors.length > 0) {
    errors.push(...typeErrors);
  }

  const rules = question.validation?.rules ?? [];
  for (const rule of rules) {
    const ruleError = validateRule(question, value, rule);
    if (ruleError) {
      errors.push(ruleError);
    }
  }

  return errors;
}

function isQuestionRequired(question: FormQuestion): boolean {
  if (
    question.type === "read_only_text" ||
    question.type === "link" ||
    question.type === "rich_text" ||
    question.type === "image" ||
    question.type === "divider" ||
    question.type === "callout"
  ) {
    return false;
  }
  return Boolean(question.required || question.validation?.required);
}

function isAnswered(question: FormQuestion, value: unknown): boolean {
  switch (question.type) {
    case "short_text":
    case "long_text":
    case "email":
    case "phone_number":
    case "date":
    case "time":
      return isStringProvided(value);
    case "read_only_text":
    case "link":
    case "rich_text":
    case "image":
    case "divider":
    case "callout":
      return true;
    case "signature":
      return hasSignatureArtifact(value);
    case "table":
      return isTableAnswered(question, value);
    case "multiple_choice_single":
      return isSingleChoiceAnswered(value);
    case "multiple_choice_multi":
      return isMultiChoiceAnswered(
        question as Extract<FormQuestion, { type: "multiple_choice_multi" }>,
        value,
      );
    case "file_upload":
      return isFileAnswered(value);
    default:
      return value !== undefined && value !== null;
  }
}

function isStringProvided(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const TIME_REGEX = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

const normaliseIsoDate = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  return ISO_DATE_REGEX.test(value) ? value : null;
};

const normaliseTime = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  return TIME_REGEX.test(value) ? value : null;
};

const timeToMinutes = (value: string): number => {
  const [hours, minutes] = value.split(":").map((part) => Number(part));
  return hours * 60 + minutes;
};

function isSingleChoiceAnswered(value: unknown): boolean {
  if (!value || typeof value !== "object") {
    return false;
  }

  const choice = value as SingleChoiceValue;
  const hasOption =
    typeof choice.optionId === "string" && choice.optionId.trim().length > 0;
  const hasOther = isStringProvided(choice.otherText);

  return hasOption || hasOther;
}

function isMultiChoiceAnswered(
  question: Extract<FormQuestion, { type: "multiple_choice_multi" }>,
  value: unknown,
): boolean {
  if (!value || typeof value !== "object") {
    return false;
  }

  const choice = value as MultiChoiceValue;
  const optionCount = Array.isArray(choice.optionIds)
    ? choice.optionIds.filter(Boolean).length
    : 0;
  const hasOther = isStringProvided(choice.otherText);
  const totalSelections = optionCount + (hasOther ? 1 : 0);

  const minSelections = question.minSelections ?? 1;
  return totalSelections >= minSelections;
}

function isFileAnswered(value: unknown): boolean {
  if (!value) {
    return false;
  }

  // Support array format (for future multi-file)
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  // Support single file object (current implementation)
  if (typeof value === "object" && "url" in value && "fileName" in value) {
    return true;
  }

  return false;
}

function validateByType(
  question: FormQuestion,
  value: unknown,
): ValidationError[] {
  switch (question.type) {
    case "email":
      return validateEmail(
        question as Extract<FormQuestion, { type: "email" }>,
        value,
      );
    case "phone_number":
      return validatePhoneNumber(
        question as Extract<FormQuestion, { type: "phone_number" }>,
        value,
      );
    case "date":
      return validateDate(
        question as Extract<FormQuestion, { type: "date" }>,
        value,
      );
    case "time":
      return validateTime(
        question as Extract<FormQuestion, { type: "time" }>,
        value,
      );
    case "multiple_choice_single":
      return validateSingleChoice(
        question as Extract<FormQuestion, { type: "multiple_choice_single" }>,
        value,
      );
    case "multiple_choice_multi":
      return validateMultiChoice(
        question as Extract<FormQuestion, { type: "multiple_choice_multi" }>,
        value,
      );
    case "table":
      return validateTable(question, value);
    default:
      return [];
  }
}

function validateTable(
  question: Extract<FormQuestion, { type: "table" }>,
  value: unknown,
): ValidationError[] {
  return validateTableAnswer(question, value).map((issue) => ({
    questionRef: question.ref,
    questionTitle: question.title,
    message: issue.message,
  }));
}

function validateEmail(
  question: Extract<FormQuestion, { type: "email" }>,
  value: unknown,
): ValidationError[] {
  if (typeof value !== "string") {
    return [
      {
        questionRef: question.ref,
        questionTitle: question.title,
        message: "Please enter a valid email address",
      },
    ];
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(value)) {
    return [
      {
        questionRef: question.ref,
        questionTitle: question.title,
        message: "Please enter a valid email address",
      },
    ];
  }

  return [];
}

function validatePhoneNumber(
  question: Extract<FormQuestion, { type: "phone_number" }>,
  value: unknown,
): ValidationError[] {
  if (typeof value !== "string") {
    return [
      {
        questionRef: question.ref,
        questionTitle: question.title,
        message: "Please enter a valid phone number",
      },
    ];
  }

  const cleaned = value.replace(/[\s\-().]/g, "");
  const isDigitsOnly = /^\+?\d+$/.test(cleaned);

  if (!isDigitsOnly) {
    return [
      {
        questionRef: question.ref,
        questionTitle: question.title,
        message: "Please enter a valid phone number",
      },
    ];
  }

  const digitsOnly = cleaned.startsWith("+") ? cleaned.slice(1) : cleaned;
  const digitCount = digitsOnly.length;

  if (question.allowInternational === false) {
    if (cleaned.startsWith("+")) {
      return [
        {
          questionRef: question.ref,
          questionTitle: question.title,
          message: "International phone numbers are not allowed",
        },
      ];
    }

    if (digitCount !== 10) {
      return [
        {
          questionRef: question.ref,
          questionTitle: question.title,
          message: "Phone number must include exactly 10 digits",
        },
      ];
    }

    return [];
  }

  if (digitCount < 10 || digitCount > 15) {
    return [
      {
        questionRef: question.ref,
        questionTitle: question.title,
        message: "Please enter a valid phone number",
      },
    ];
  }

  return [];
}

function validateDate(
  question: Extract<FormQuestion, { type: "date" }>,
  value: unknown,
): ValidationError[] {
  const normalizedValue = normaliseIsoDate(value);
  if (!normalizedValue) {
    return [
      {
        questionRef: question.ref,
        questionTitle: question.title,
        message: "Please enter a valid date",
      },
    ];
  }

  // Validate min date constraint
  const minDate = normaliseIsoDate(question.minDate);
  if (minDate) {
    if (normalizedValue < minDate) {
      return [
        {
          questionRef: question.ref,
          questionTitle: question.title,
          message: `Date must be on or after ${minDate}`,
        },
      ];
    }
  }

  // Validate max date constraint
  const maxDate = normaliseIsoDate(question.maxDate);
  if (maxDate) {
    if (normalizedValue > maxDate) {
      return [
        {
          questionRef: question.ref,
          questionTitle: question.title,
          message: `Date must be on or before ${maxDate}`,
        },
      ];
    }
  }

  return [];
}

function validateTime(
  question: Extract<FormQuestion, { type: "time" }>,
  value: unknown,
): ValidationError[] {
  const normalizedValue = normaliseTime(value);
  if (!normalizedValue) {
    return [
      {
        questionRef: question.ref,
        questionTitle: question.title,
        message: "Please enter a valid time",
      },
    ];
  }

  const minTime = normaliseTime(question.minTime);
  if (minTime) {
    if (timeToMinutes(normalizedValue) < timeToMinutes(minTime)) {
      return [
        {
          questionRef: question.ref,
          questionTitle: question.title,
          message: `Time must be on or after ${minTime}`,
        },
      ];
    }
  }

  const maxTime = normaliseTime(question.maxTime);
  if (maxTime) {
    if (timeToMinutes(normalizedValue) > timeToMinutes(maxTime)) {
      return [
        {
          questionRef: question.ref,
          questionTitle: question.title,
          message: `Time must be on or before ${maxTime}`,
        },
      ];
    }
  }

  return [];
}

function validateSingleChoice(
  question: Extract<FormQuestion, { type: "multiple_choice_single" }>,
  value: unknown,
): ValidationError[] {
  if (!value || typeof value !== "object") {
    return [
      {
        questionRef: question.ref,
        questionTitle: question.title,
        message: "Selected option is invalid",
      },
    ];
  }

  const choice = value as SingleChoiceValue;
  const errors: ValidationError[] = [];

  if (choice.optionId) {
    const optionExists = question.options.some(
      (option) => option.id === choice.optionId,
    );
    if (!optionExists) {
      errors.push({
        questionRef: question.ref,
        questionTitle: question.title,
        message: "Selected option is invalid",
      });
    }
  }

  if (choice.otherText) {
    if (!question.allowOther) {
      errors.push({
        questionRef: question.ref,
        questionTitle: question.title,
        message: "Custom answer is not allowed for this question",
      });
    } else if (!isStringProvided(choice.otherText)) {
      errors.push({
        questionRef: question.ref,
        questionTitle: question.title,
        message: "Custom answer cannot be empty",
      });
    }
  }

  return errors;
}

function validateMultiChoice(
  question: Extract<FormQuestion, { type: "multiple_choice_multi" }>,
  value: unknown,
): ValidationError[] {
  if (!value || typeof value !== "object") {
    return [
      {
        questionRef: question.ref,
        questionTitle: question.title,
        message: "Selection is invalid",
      },
    ];
  }

  const choice = value as MultiChoiceValue;
  const errors: ValidationError[] = [];
  const selectedOptionIds = Array.isArray(choice.optionIds)
    ? choice.optionIds
    : [];

  for (const optionId of selectedOptionIds) {
    const optionExists = question.options.some(
      (option) => option.id === optionId,
    );
    if (!optionExists) {
      errors.push({
        questionRef: question.ref,
        questionTitle: question.title,
        message: `Selected option "${optionId}" is invalid`,
      });
    }
  }

  const optionCount = selectedOptionIds.length;
  const hasOther = isStringProvided(choice.otherText);
  const totalSelections = optionCount + (hasOther ? 1 : 0);

  if (question.minSelections && totalSelections < question.minSelections) {
    errors.push({
      questionRef: question.ref,
      questionTitle: question.title,
      message: `Please select at least ${question.minSelections} option${
        question.minSelections > 1 ? "s" : ""
      }`,
    });
  }

  if (question.maxSelections && totalSelections > question.maxSelections) {
    errors.push({
      questionRef: question.ref,
      questionTitle: question.title,
      message: `Please select no more than ${question.maxSelections} option${
        question.maxSelections > 1 ? "s" : ""
      }`,
    });
  }

  if (choice.otherText && !question.allowOther) {
    errors.push({
      questionRef: question.ref,
      questionTitle: question.title,
      message: "Custom answer is not allowed for this question",
    });
  }

  if (
    choice.otherText &&
    question.allowOther &&
    !isStringProvided(choice.otherText)
  ) {
    errors.push({
      questionRef: question.ref,
      questionTitle: question.title,
      message: "Custom answer cannot be empty",
    });
  }

  return errors;
}

function validateRule(
  question: FormQuestion,
  value: unknown,
  rule: QuestionValidation["rules"][number],
): ValidationError | null {
  switch (rule.kind) {
    case "length":
      return validateLengthRule(question, value, rule);
    case "pattern":
      return validatePatternRule(question, value, rule);
    case "numeric":
      return validateNumericRule(question, value, rule);
    case "file":
    default:
      return null;
  }
}

function validateLengthRule(
  question: FormQuestion,
  value: unknown,
  rule: Extract<QuestionValidation["rules"][number], { kind: "length" }>,
): ValidationError | null {
  if (typeof value !== "string") {
    return null;
  }

  const length = value.length;

  if (rule.min !== undefined && length < rule.min) {
    return {
      questionRef: question.ref,
      questionTitle: question.title,
      message: rule.message || `Minimum length is ${rule.min} characters`,
    };
  }

  if (rule.max !== undefined && length > rule.max) {
    return {
      questionRef: question.ref,
      questionTitle: question.title,
      message: rule.message || `Maximum length is ${rule.max} characters`,
    };
  }

  return null;
}

function validatePatternRule(
  question: FormQuestion,
  value: unknown,
  rule: Extract<QuestionValidation["rules"][number], { kind: "pattern" }>,
): ValidationError | null {
  if (typeof value !== "string") {
    return null;
  }

  try {
    const regex = new RegExp(rule.pattern, rule.flags);
    if (!regex.test(value)) {
      return {
        questionRef: question.ref,
        questionTitle: question.title,
        message: rule.message || "Invalid format",
      };
    }
  } catch {
    return null;
  }

  return null;
}

function validateNumericRule(
  question: FormQuestion,
  value: unknown,
  rule: Extract<QuestionValidation["rules"][number], { kind: "numeric" }>,
): ValidationError | null {
  const num = typeof value === "number" ? value : Number(value);

  if (Number.isNaN(num)) {
    return {
      questionRef: question.ref,
      questionTitle: question.title,
      message: "Please enter a valid number",
    };
  }

  if (rule.min !== undefined && num < rule.min) {
    return {
      questionRef: question.ref,
      questionTitle: question.title,
      message: rule.message || `Minimum value is ${rule.min}`,
    };
  }

  if (rule.max !== undefined && num > rule.max) {
    return {
      questionRef: question.ref,
      questionTitle: question.title,
      message: rule.message || `Maximum value is ${rule.max}`,
    };
  }

  return null;
}
